import type { AddToCartItem, ShopifyCart, ShopifyCartItem } from '../types/servicesShopifyCart';
import type { CartActionPlan, CartItemQuantity, CartLineRemoval } from '../types/models';
import {
  getFamilyTotalQuantity,
  type FamilyCountLine,
  type VariantParentMaxQuantityFamily,
} from '../cartRules/variantParentMaxQuantity';
import { getLineIdentity, getLineQuantities } from './lineItemProperties';
import {
  getCartStockUsage,
  getConstrainedVariants,
  getRestorableQuantities,
  orderByStockPriority,
  planStockRelease,
  type CartStockLine,
  type StockLineGroups,
} from '@/features/Product/services/productAvailability';

export type StockSwap = StockLineGroups & {
  isReplacement: boolean;
  releases: CartItemQuantity[];
  constrainedVariants: Set<number>;
  exactStock: Map<number, number>;
};

export type ProjectedCartFit = 'fits' | 'stock-short' | 'family-limit';

export type PendingStockSwap = {
  swap: StockSwap;
  items: AddToCartItem[];
  releasedCart: ShopifyCart | null;
  isAddSent: boolean;
  acceptedQuantities: number[] | null;
};

type StockLineOptions = {
  isFreeGiftItem: (item: ShopifyCartItem) => boolean;
  unavailableLineKeys: ReadonlySet<string>;
  protectedLineIdentities: ReadonlySet<string>;
};

const toStockLine = ({ key, variant_id, quantity, properties }: CartStockLine): CartStockLine => ({
  key,
  variant_id,
  quantity,
  properties: { ...properties },
});

const getIdentities = (lines: CartStockLine[]): Set<string> =>
  new Set(lines.map(({ variant_id, properties }) => getLineIdentity(variant_id, properties)));

const toRequestedLines = (items: AddToCartItem[]): CartStockLine[] =>
  items.map(({ id, quantity, properties }, index) => ({
    key: `requested:${index}`,
    variant_id: id,
    quantity,
    properties: { ...properties },
  }));

const getRemovedQuantities = (removals: CartLineRemoval[]): Map<string, number> =>
  new Map(removals.map(({ key, removeQuantity }) => [key, removeQuantity]));

const getRetainedLines = (cart: ShopifyCart, removals: CartLineRemoval[]): ShopifyCartItem[] => {
  const removedQuantities = getRemovedQuantities(removals);
  return cart.items
    .map((line) => ({ ...line, quantity: line.quantity - (removedQuantities.get(line.key) ?? 0) }))
    .filter(({ quantity }) => quantity > 0);
};

export const groupStockLines = (
  items: AddToCartItem[],
  cart: ShopifyCart,
  { isFreeGiftItem, unavailableLineKeys, protectedLineIdentities }: StockLineOptions,
): StockLineGroups => {
  const requestedLines = toRequestedLines(items);
  const requestedIdentities = getIdentities(requestedLines);
  const isFixed = (item: ShopifyCartItem) =>
    isFreeGiftItem(item) ||
    unavailableLineKeys.has(item.key) ||
    requestedIdentities.has(getLineIdentity(item.variant_id, item.properties));
  return {
    requestedLines,
    fixedLines: cart.items.filter(isFixed).map(toStockLine),
    releasableLines: orderByStockPriority(
      cart.items.filter((item) => !isFixed(item)).map(toStockLine),
      protectedLineIdentities,
    ),
  };
};

export const sharesTrackedStock = (
  { requestedLines, releasableLines }: StockLineGroups,
  isExactStock: (variantId: number) => boolean,
): boolean => {
  const releasableUsage = getCartStockUsage(releasableLines);
  return [...getCartStockUsage(requestedLines).keys()].some(
    (variantId) => releasableUsage.has(variantId) && isExactStock(variantId),
  );
};

export const planStockSwap = (
  groups: StockLineGroups,
  getExactStock: (variantId: number) => number | undefined,
): StockSwap | null => {
  const constrainedVariants = getConstrainedVariants(groups.requestedLines, getExactStock);
  const exactStock = new Map(
    [...constrainedVariants].map((variantId) => [variantId, getExactStock(variantId) ?? 0]),
  );
  const releases = planStockRelease(groups, constrainedVariants, (variantId) =>
    exactStock.get(variantId),
  );
  return releases?.length
    ? { ...groups, isReplacement: false, releases, constrainedVariants, exactStock }
    : null;
};

export const areRemovalsCurrent = (cart: ShopifyCart, removals: CartLineRemoval[]): boolean =>
  removals.every(({ key, lineIdentity, removeQuantity }) => {
    const line = cart.items.find((item) => item.key === key);
    return (
      !!line &&
      getLineIdentity(line.variant_id, line.properties) === lineIdentity &&
      line.quantity >= removeQuantity
    );
  });

const fitsProjectedStock = (
  requestedLines: CartStockLine[],
  retainedLines: ShopifyCartItem[],
  getExactStock: (variantId: number) => number | undefined,
): boolean =>
  planStockRelease(
    { requestedLines, fixedLines: retainedLines.map(toStockLine), releasableLines: [] },
    getConstrainedVariants(requestedLines, getExactStock),
    getExactStock,
  ) !== null;

const fitsProjectedFamilies = (
  addedLines: FamilyCountLine[],
  retainedLines: ShopifyCartItem[],
  families: VariantParentMaxQuantityFamily[],
): boolean =>
  families
    .filter((family) => getFamilyTotalQuantity(addedLines, family) > 0)
    .every(
      (family) =>
        getFamilyTotalQuantity([...retainedLines, ...addedLines], family) <= family.maxQuantity,
    );

export const fitsProjectedCart = (
  items: AddToCartItem[],
  cart: ShopifyCart,
  { removals, additionSkus }: CartActionPlan,
  getExactStock: (variantId: number) => number | undefined,
  families: VariantParentMaxQuantityFamily[],
): ProjectedCartFit => {
  const requestedLines = toRequestedLines(items);
  const retainedLines = getRetainedLines(cart, removals);
  const addedLines = requestedLines.map(({ quantity, properties }, index) => ({
    sku: additionSkus[index] ?? '',
    quantity,
    properties,
  }));
  if (!fitsProjectedStock(requestedLines, retainedLines, getExactStock)) return 'stock-short';
  if (!fitsProjectedFamilies(addedLines, retainedLines, families)) return 'family-limit';
  return 'fits';
};

export const planRemovalSwap = (
  items: AddToCartItem[],
  cart: ShopifyCart,
  removals: CartLineRemoval[],
  getExactStock: (variantId: number) => number | undefined,
): StockSwap | null => {
  if (!removals.length) return null;
  const requestedLines = toRequestedLines(items);
  const removedQuantities = getRemovedQuantities(removals);
  const isRemoved = ({ key }: ShopifyCartItem) => removedQuantities.has(key);
  const releasableLines = cart.items.filter(isRemoved).map(toStockLine);
  const constrainedVariants = getConstrainedVariants(requestedLines, getExactStock);
  return {
    requestedLines,
    fixedLines: cart.items.filter((line) => !isRemoved(line)).map(toStockLine),
    releasableLines,
    isReplacement: true,
    releases: releasableLines.map(({ key, quantity }) => ({
      key,
      quantity: quantity - (removedQuantities.get(key) ?? 0),
    })),
    constrainedVariants,
    exactStock: new Map(
      [...constrainedVariants].map((variantId) => [variantId, getExactStock(variantId) ?? 0]),
    ),
  };
};

const isCompleteAdd = ({ requestedLines }: StockSwap, acceptedQuantities: number[]): boolean =>
  requestedLines.every(({ quantity }, index) => (acceptedQuantities[index] ?? 0) >= quantity);

export const hasReleasedCapacity = (
  { requestedLines, constrainedVariants, exactStock }: StockSwap,
  cart: ShopifyCart,
): boolean => {
  const cartUsage = getCartStockUsage(cart.items);
  const requestedUsage = getCartStockUsage(requestedLines);
  return [...constrainedVariants].every(
    (variantId) =>
      (exactStock.get(variantId) ?? 0) - (cartUsage.get(variantId) ?? 0) >=
      (requestedUsage.get(variantId) ?? 0),
  );
};

export const getRestoreItems = (
  swap: StockSwap,
  acceptedQuantities: number[],
  cart: ShopifyCart,
): AddToCartItem[] => {
  const acceptedLines = swap.requestedLines
    .map((line, index) => ({ ...line, quantity: acceptedQuantities[index] ?? 0 }))
    .filter(({ quantity }) => quantity > 0);
  const restorableQuantities = getRestorableQuantities(
    { ...swap, requestedLines: acceptedLines },
    swap.releases,
    swap.constrainedVariants,
    (variantId) => swap.exactStock.get(variantId),
  );
  const currentQuantities = getLineQuantities(cart.items);
  const keepsReplacement = swap.isReplacement && isCompleteAdd(swap, acceptedQuantities);
  return swap.releases.flatMap(({ key, quantity: releasedQuantity }) => {
    const line = swap.releasableLines.find((releasableLine) => releasableLine.key === key);
    if (!line) return [];
    const stockTargetQuantity = releasedQuantity + (restorableQuantities.get(key) ?? 0);
    const replacementTargetQuantity = keepsReplacement ? releasedQuantity : line.quantity;
    const targetQuantity = swap.isReplacement ? replacementTargetQuantity : stockTargetQuantity;
    const currentQuantity =
      currentQuantities.get(getLineIdentity(line.variant_id, line.properties)) ?? 0;
    const restoreQuantity = targetQuantity - currentQuantity;
    return restoreQuantity > 0
      ? [{ id: line.variant_id, quantity: restoreQuantity, properties: line.properties }]
      : [];
  });
};

export const getPreAddLineQuantities = (
  swap: StockSwap,
  acceptedQuantities: number[],
): Map<string, number> => {
  const { requestedLines, fixedLines, releasableLines } = swap;
  const requestedIdentities = getIdentities(requestedLines);
  const isIntentionallyReplaced = swap.isReplacement && isCompleteAdd(swap, acceptedQuantities);
  return getLineQuantities(
    [...fixedLines, ...(isIntentionallyReplaced ? [] : releasableLines)].filter(
      ({ variant_id, properties }) =>
        !requestedIdentities.has(getLineIdentity(variant_id, properties)),
    ),
  );
};
